// Captura de atribucion primer-toque + ultimo-toque para la SPA de TFD.
// posthog-js adjunta utm_* a los eventos automaticamente, pero NO persiste el
// primer toque a traves de visitas ni escribe columnas a founder_applications.
// Esto llena ese hueco.
//
// - Primer toque: localStorage, ventana 90 dias, set_once en la persona. Nunca se pisa.
// - Ultimo toque: sessionStorage, set en la persona en cada sesion.
// - getAttributionForApplication(): lo que el modal escribe a founder_applications.

import type { PostHogInterface } from "posthog-js";

const FIRST_KEY = "tfd_attr_first";
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  landing_page?: string;
  referrer?: string;
  ts: number;
};

function readUrl(): Attribution {
  const p = new URLSearchParams(window.location.search);
  const a: Attribution = { ts: Date.now() };
  for (const k of UTM_KEYS) {
    const v = p.get(k);
    if (v) (a as Record<string, string | number>)[k] = v.slice(0, 120);
  }
  const gclid = p.get("gclid");
  const fbclid = p.get("fbclid");
  if (gclid) a.gclid = gclid.slice(0, 200);
  if (fbclid) a.fbclid = fbclid.slice(0, 200);
  a.landing_page = window.location.pathname;
  a.referrer = document.referrer || undefined;
  return a;
}

function hasUtm(a: Attribution): boolean {
  return UTM_KEYS.some((k) => (a as Record<string, unknown>)[k]) || !!a.gclid || !!a.fbclid;
}

/** Llamar una vez en el init de PostHog. */
export function captureAttribution(ph: PostHogInterface) {
  if (typeof window === "undefined") return;
  const now = readUrl();

  // Primer toque (set_once, ventana 90 dias).
  let first: Attribution | null = null;
  try {
    const raw = localStorage.getItem(FIRST_KEY);
    if (raw) first = JSON.parse(raw) as Attribution;
  } catch {
    first = null;
  }
  const firstExpired = first && Date.now() - first.ts > NINETY_DAYS_MS;
  if ((!first || firstExpired) && hasUtm(now)) {
    first = now;
    try {
      localStorage.setItem(FIRST_KEY, JSON.stringify(now));
    } catch {
      /* storage bloqueado: seguimos, no rompemos la carga */
    }
  }

  // Ultimo toque de esta sesion.
  if (hasUtm(now)) {
    try {
      sessionStorage.setItem("tfd_attr_last", JSON.stringify(now));
    } catch {
      /* ignore */
    }
  }

  // Escribir a la persona de PostHog.
  if (first) {
    ph.setPersonPropertiesForFlags?.({});
    ph.people?.set_once?.({
      first_utm_source: first.utm_source,
      first_utm_medium: first.utm_medium,
      first_utm_campaign: first.utm_campaign,
      first_utm_content: first.utm_content,
      first_utm_term: first.utm_term,
      first_landing_page: first.landing_page,
      first_referrer: first.referrer,
    });
  }
  if (hasUtm(now)) {
    ph.people?.set?.({
      latest_utm_source: now.utm_source,
      latest_utm_medium: now.utm_medium,
      latest_utm_campaign: now.utm_campaign,
      latest_utm_content: now.utm_content,
      latest_utm_term: now.utm_term,
    });
  }
}

/**
 * Las columnas de atribucion que founder_applications puede persistir
 * (migracion 20260729020000). Exactamente el shape del insert: sin `ts`
 * (interno) y sin `utm_source` — la columna `source` ya tiene su propio
 * path de captura en el modal y se queda como esta.
 */
export type ApplicationAttribution = {
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  landing_page?: string;
  referrer?: string;
  gclid?: string;
  fbclid?: string;
};

const APPLICATION_COLUMNS = [
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "landing_page",
  "referrer",
  "gclid",
  "fbclid",
] as const;

/** Lo que FounderIntakeModal escribe a founder_applications: el primer toque, mapeado a columnas. */
export function getAttributionForApplication(): ApplicationAttribution {
  try {
    const raw = localStorage.getItem(FIRST_KEY);
    if (!raw) return {};
    const first = JSON.parse(raw) as Attribution;
    const out: ApplicationAttribution = {};
    for (const k of APPLICATION_COLUMNS) {
      const v = first[k];
      if (typeof v === "string" && v) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}
